import React from "react";
import { Box, Typography, Chip, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, CircularProgress } from "@mui/material";
import { X, Download } from "lucide-react";
import configJson from "../api/config.json";
import { ReportSnapshot, SnapshotCell, SnapshotGroup, SnapshotRow } from "../util/report-snapshot";

type Env = 'development' | 'production';
const config = configJson as Record<Env, { protocol: string; domain: string; port: number }>;

const getApiBase = (): string => {
    const env = (process.env.REACT_APP_ENV || 'production') as Env;
    if (env === 'development') {
        const { protocol, domain, port } = config[env];
        const isDefault = (protocol === 'http' && port === 80) || (protocol === 'https' && port === 443);
        return isDefault ? `${protocol}://${domain}` : `${protocol}://${domain}:${port}`;
    }
    const { protocol, domain } = config.production;
    return `${protocol}://${domain}`;
};

const API_BASE = getApiBase();

// Renders a frozen ReportSnapshot faithfully to how the live Forecast /
// Forecast vs Actual pages look: sub-headings collapse/expand (starting
// collapsed, same default as the source pages), and the "Other" group shows
// as a one-line collapsed summary until expanded - just like the source pages.
// The only thing NOT reproduced is click-to-drill-into-simulation-detail,
// since that needs the underlying transaction data, which isn't part of the
// snapshot.

const isForecastActual = (cell: SnapshotCell | undefined): cell is { forecast: string; actual: string } =>
    !!cell && typeof cell === 'object';

// Each group renders its own separate <table> (matching the source pages,
// which render one DynamicTable per group) - without fixed, shared column
// widths, the browser auto-sizes each table independently and columns don't
// line up between groups. table-layout: fixed + an explicit <colgroup> forces
// every group's table to use identical column widths, same as the source
// pages' `size: 150` / `size: 240` bucket columns.
const LABEL_COL_WIDTH = 200;
const FORECAST_BUCKET_WIDTH = 150;
const FORECAST_VS_ACTUAL_BUCKET_WIDTH = 240;

interface SharedReportViewProps {
    snapshot: ReportSnapshot;
    slug: string;
    accessToken: string | null;
    title: string;
}

// Filesystem-unsafe characters (matches Windows' reserved set, the strictest
// common case) stripped from the document title before using it as a
// download filename.
const sanitizeFileName = (name: string): string =>
    name.replace(/[\\/:*?"<>|]/g, '').trim() || 'Report';

const GroupTable: React.FC<{
    group: SnapshotGroup;
    buckets: { id: string; label: string }[];
    isForecastVsActual: boolean;
    expandedRows: Set<number>;
    onToggleRow: (rowIndex: number) => void;
}> = ({ group, buckets, isForecastVsActual, expandedRows, onToggleRow }) => {
    const theme = useTheme();
    // Sub-heading rows hide their own following isNested rows until expanded -
    // walk the rows in order, tracking whether we're currently inside a
    // collapsed section (mirrors the source pages' array layout: a group-label
    // row is always immediately followed by its own leaf rows, then its subtotal).
    const visibleRows: { row: SnapshotRow; index: number }[] = [];
    let hideUntilNonNested = false;
    group.rows.forEach((row, index) => {
        if (row.isGroupLabel) {
            hideUntilNonNested = !expandedRows.has(index);
            visibleRows.push({ row, index });
            return;
        }
        if (row.isNested && hideUntilNonNested) return;
        if (!row.isNested) hideUntilNonNested = false;
        visibleRows.push({ row, index });
    });

    const bucketWidth = isForecastVsActual ? FORECAST_VS_ACTUAL_BUCKET_WIDTH : FORECAST_BUCKET_WIDTH;
    const tableWidth = LABEL_COL_WIDTH + buckets.length * bucketWidth;
    // A landscape page (A4/Letter) has roughly ~1050px of printable width at
    // 96dpi after the @page margins - if this table is wider than that (many
    // month columns), scale it down for print so nothing falls off the page.
    // This is a best-effort fallback, not a guarantee for every browser: very
    // wide tables (e.g. "by Month" over a multi-year horizon) print more
    // reliably by switching the period to "by Quarter" or "by Year" first.
    const PRINTABLE_WIDTH_PX = 1050;
    const printScale = Math.min(1, PRINTABLE_WIDTH_PX / tableWidth);

    return (
        <Box className="report-table-scroll" sx={{
            overflowX: 'auto',
            '@media print': {
                overflow: 'visible',
                ...(printScale < 1 ? { transform: `scale(${printScale})`, transformOrigin: 'top left' } : {}),
            },
        }}>
            <Box component="table" sx={{ width: tableWidth, tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: 13 }}>
                <colgroup>
                    <col style={{ width: LABEL_COL_WIDTH }} />
                    {buckets.map(b => <col key={b.id} style={{ width: bucketWidth }} />)}
                </colgroup>
                <Box component="thead">
                    <Box component="tr" sx={{ background: 'rgba(var(--color-primary-rgb), 0.1)', borderBottom: '2px solid var(--color-primary)' }}>
                        <Box component="th" sx={{ textAlign: 'left', p: 1, fontWeight: 700, color: 'var(--color-primary)', whiteSpace: 'nowrap' }}>
                            {group.label.toUpperCase()}
                        </Box>
                        {buckets.map(b => (
                            <Box component="th" key={b.id} sx={{ textAlign: 'right', p: 1, fontWeight: 700, color: 'var(--color-primary)', whiteSpace: 'nowrap' }}>
                                {b.label}
                                {isForecastVsActual && (
                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', fontSize: 11, fontStyle: 'italic', fontWeight: 400 }}>
                                        <span>Forecast</span><span>vs</span><span>Actual</span>
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Box>
                </Box>
                <Box component="tbody">
                    {visibleRows.map(({ row, index }, visibleIdx) => (
                        <Box
                            component="tr"
                            key={index}
                            onClick={row.isGroupLabel ? () => onToggleRow(index) : undefined}
                            sx={{
                                background: row.groupColor
                                    ? `${row.groupColor}${row.isGroupLabel || row.isSubtotal ? '29' : '0f'}`
                                    : row.isTotal
                                        ? `${theme.palette.primary.main}40`
                                        : visibleIdx % 2 !== 0 ? 'var(--color-container-background)' : 'var(--color-page-background)',
                                borderTop: '1px solid #f0f0f0',
                                cursor: row.isGroupLabel ? 'pointer' : 'default',
                                userSelect: row.isGroupLabel ? 'none' : 'auto',
                            }}
                        >
                            <Box component="td" sx={{
                                p: 1, pl: row.isNested ? 4 : 1.25,
                                fontWeight: row.isTotal || row.isGroupLabel || row.isSubtotal ? 700 : 400,
                                fontStyle: row.isGroupLabel ? 'italic' : 'normal',
                                color: row.isGroupLabel && row.groupColor ? row.groupColor : undefined,
                                whiteSpace: 'nowrap',
                            }}>
                                {row.isGroupLabel && (
                                    <Typography component="span" sx={{ fontSize: 10, width: 10, display: 'inline-block', mr: 0.75 }}>
                                        {expandedRows.has(index) ? '▼' : '▶'}
                                    </Typography>
                                )}
                                {row.label}
                            </Box>
                            {row.isGroupLabel ? (
                                <Box component="td" colSpan={buckets.length} />
                            ) : buckets.map(b => {
                                const cell = row.cells[b.id];
                                const isAffected = row.affectedBucketIds?.includes(b.id);
                                return (
                                    <Box component="td" key={b.id} sx={{ p: 1, textAlign: 'right', position: 'relative', whiteSpace: 'nowrap' }}>
                                        {isAffected && (
                                            <Box sx={{
                                                position: 'absolute', top: 0, right: 0, width: 0, height: 0,
                                                borderStyle: 'solid', borderWidth: '0 14px 14px 0',
                                                borderColor: 'transparent #d32f2f transparent transparent',
                                            }} />
                                        )}
                                        {isForecastActual(cell) ? (
                                            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
                                                <Typography component="span" sx={{ fontSize: 13, color: '#454545', fontWeight: row.isTotal || row.isSubtotal ? 700 : 400 }}>
                                                    {cell.forecast}
                                                </Typography>
                                                <Typography component="span" sx={{ fontSize: 13, fontWeight: row.isTotal || row.isSubtotal ? 700 : 600 }}>
                                                    {cell.actual}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Typography component="span" sx={{ fontSize: 13, fontWeight: row.isTotal || row.isSubtotal ? 700 : 400 }}>
                                                {typeof cell === 'string' ? cell : ''}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

const SharedReportView: React.FC<SharedReportViewProps> = ({ snapshot, slug, accessToken, title }) => {
    const isForecastVsActual = snapshot.reportType === 'forecast-vs-actual';

    // Keyed by group label -> set of row indexes (within that group) that are expanded
    const [expandedByGroup, setExpandedByGroup] = React.useState<Record<string, Set<number>>>({});
    const [otherExpanded, setOtherExpanded] = React.useState(false);
    const [simulationDetailOpen, setSimulationDetailOpen] = React.useState(false);
    const [isDownloading, setIsDownloading] = React.useState(false);

    const toggleRow = (groupLabel: string, rowIndex: number) => {
        setExpandedByGroup(prev => {
            const current = new Set(prev[groupLabel] || []);
            if (current.has(rowIndex)) current.delete(rowIndex);
            else current.add(rowIndex);
            return { ...prev, [groupLabel]: current };
        });
    };

    // The snapshot doesn't carry the simulation's internal rules (those
    // reference internal provision IDs that mean nothing outside the app),
    // but every row already knows which of its own periods the simulation
    // actually changed (affectedBucketIds - the same data driving the red
    // corner-triangle indicators) - surface that as the "what did this
    // simulation actually do" detail instead. Leaf rows only, so a rolled-up
    // TOTAL/Subtotal line doesn't just repeat what's already listed under it.
    const affectedSummary = React.useMemo(() => {
        const rows: { groupLabel: string; rowLabel: string; bucketLabels: string[] }[] = [];
        snapshot.groups.forEach(group => {
            group.rows.forEach(row => {
                if (row.isGroupLabel || row.isTotal || row.isSubtotal) return;
                if (!row.affectedBucketIds?.length) return;
                const bucketLabels = row.affectedBucketIds
                    .map(id => snapshot.buckets.find(b => b.id === id)?.label)
                    .filter((l): l is string => !!l);
                if (bucketLabels.length) rows.push({ groupLabel: group.label, rowLabel: row.label, bucketLabels });
            });
        });
        return rows;
    }, [snapshot]);

    // Server-rendered PDF (small, dynamically-sized fonts/columns so wide
    // reports still fit a page) - always fully expanded, since a downloaded
    // document is meant to be complete regardless of what's collapsed on screen.
    const handleDownloadPdf = async () => {
        setIsDownloading(true);
        try {
            const headers: Record<string, string> = {};
            if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

            const response = await fetch(`${API_BASE}/documents/${slug}/pdf`, { headers });
            if (!response.ok) throw new Error('Failed to generate PDF');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${sanitizeFileName(title)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            window.alert('Failed to download PDF. Please try again.');
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: snapshot.appliedSimulationName ? 1 : 2 }}>
                <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                    {snapshot.periodLabel} — snapshot taken {new Date(snapshot.generatedAt).toLocaleString()}
                </Typography>
                <Button
                    size="small"
                    onClick={handleDownloadPdf}
                    disabled={isDownloading}
                    startIcon={isDownloading ? <CircularProgress size={14} sx={{ color: 'var(--color-primary)' }} /> : <Download size={14} />}
                    sx={{ fontSize: 12, textTransform: 'none', color: 'var(--color-primary)', flexShrink: 0 }}
                >
                    {isDownloading ? 'Preparing…' : 'Download PDF'}
                </Button>
            </Box>
            {snapshot.appliedSimulationName && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <Chip
                        label={`Simulation applied: ${snapshot.appliedSimulationName}`}
                        size="small"
                        onClick={() => setSimulationDetailOpen(true)}
                        sx={{ fontSize: 11, height: 22, background: 'rgba(var(--color-primary-rgb), 0.12)', color: 'var(--color-primary)', cursor: 'pointer', '&:hover': { background: 'rgba(var(--color-primary-rgb), 0.2)' } }}
                    />
                </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: isForecastVsActual ? 3 : 0, justifyContent: 'center', mb: 2 }}>
                {isForecastVsActual && isForecastActual(snapshot.overallTotal) ? (
                    <>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography sx={{ fontSize: 12, color: 'var(--color-primary)' }}>FORECAST</Typography>
                            <Typography sx={{ fontSize: 20, fontWeight: 500 }}>{snapshot.overallTotal.forecast}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography sx={{ fontSize: 12, color: 'var(--color-primary)', fontWeight: 600 }}>ACTUAL</Typography>
                            <Typography sx={{ fontSize: 20, fontWeight: 600 }}>{snapshot.overallTotal.actual}</Typography>
                        </Box>
                    </>
                ) : !isForecastVsActual && typeof snapshot.overallTotal === 'string' ? (
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 12, color: 'var(--color-primary)' }}>OVERALL TOTAL FOR ALL MAJOR HEADINGS</Typography>
                        <Typography sx={{ fontSize: 20, fontWeight: 600 }}>{snapshot.overallTotal}</Typography>
                    </Box>
                ) : null}
            </Box>

            {snapshot.groups.map(group => {
                const isOther = group.label === 'Other';

                if (isOther && !otherExpanded) {
                    return (
                        <Box
                            key={group.label}
                            onClick={() => setOtherExpanded(true)}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', userSelect: 'none',
                                border: '1px solid #e0e0e0', borderRadius: 1, px: 2, py: 1, mb: 1, backgroundColor: 'var(--color-container-background)',
                            }}
                        >
                            <Typography sx={{ fontSize: 10 }}>▶</Typography>
                            <Typography sx={{ fontWeight: 600, flex: 1 }}>OTHER</Typography>
                            <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                                {isForecastActual(snapshot.otherTotal)
                                    ? `Forecast ${snapshot.otherTotal.forecast} / Actual ${snapshot.otherTotal.actual}`
                                    : snapshot.otherTotal}
                            </Typography>
                        </Box>
                    );
                }

                return (
                    <Box key={group.label} sx={{ mb: 2 }}>
                        {isOther && (
                            <Box
                                onClick={() => setOtherExpanded(false)}
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', mb: 0.5 }}
                            >
                                <Typography sx={{ fontSize: 10 }}>▼</Typography>
                                <Typography sx={{ fontWeight: 600 }}>OTHER</Typography>
                            </Box>
                        )}
                        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                            <GroupTable
                                group={group}
                                buckets={snapshot.buckets}
                                isForecastVsActual={isForecastVsActual}
                                expandedRows={expandedByGroup[group.label] || new Set()}
                                onToggleRow={(rowIndex) => toggleRow(group.label, rowIndex)}
                            />
                        </Box>
                    </Box>
                );
            })}

            <Dialog open={simulationDetailOpen} onClose={() => setSimulationDetailOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 700, fontSize: 18, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {snapshot.appliedSimulationName}
                    <IconButton size="small" onClick={() => setSimulationDetailOpen(false)}>
                        <X size={16} />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 0 }}>
                    {affectedSummary.length === 0 ? (
                        <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                            This simulation didn't change any values in the period shown.
                        </Typography>
                    ) : (
                        <>
                            <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', mb: 1.5 }}>
                                This simulation changed the following, compared to the un-simulated numbers:
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {affectedSummary.map((item, idx) => (
                                    <Box key={idx} sx={{ p: 1, borderRadius: 2, background: 'var(--color-container-background)' }}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{item.rowLabel}</Typography>
                                        <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                                            {item.groupLabel} — affected in: {item.bucketLabels.join(', ')}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setSimulationDetailOpen(false)}
                        variant="contained"
                        sx={{ background: 'var(--color-primary)', color: '#fff', boxShadow: 'none', borderRadius: 2, fontWeight: 600, '&:hover': { background: 'var(--color-primary-hover)' } }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SharedReportView;
